import { CommonModule, CurrencyPipe } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, computed, signal } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { BillingService, roundMoney } from '@app/modules/payments/services/billing.service'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { BillingPaymentMethod } from '@app/v2/settings/components/billing-payments/billing/payment-cards-modal/payment-cards-modal.component'
import { WalletTopUpModalComponent } from '@app/v2/settings/components/billing-payments/billing/wallet-top-up-modal/wallet-top-up-modal.component'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { NavbarService } from '@app/v2/shared/components/navbar/navbar.service'
import { listenLayeredOverlayDismiss } from '@app/v2/shared/helpers/layered-overlay-dismiss'
import { formHasPhoneNumberElement } from '@database/models/resend-confirmation-pricing'
import { DatabaseService, ParentRegistrationOption } from '@database/services/database.service'
import { NbButtonModule, NbCardModule, NbCheckboxModule, NbDialogRef, NbIconModule, NbToastrService, NbTooltipModule } from '@nebular/theme'
import { InfiniteScrollModule } from 'ngx-infinite-scroll'
import { Subject, of } from 'rxjs'
import { catchError, debounceTime, delay, repeatWhen, take, takeUntil, takeWhile } from 'rxjs/operators'
import { RegistrationsDataSourceCollection } from '../../models/registrations.datasource-collection'
interface ColumnMapping {
  sourceColumn: string
  targetColumn: string
  isRequired: boolean
}

// Add UploadState enum
export enum UploadState {
  UPLOADED = 'uploaded',
  COMPILING = 'compiling',
  COMPILED = 'compiled',
  SAVING = 'saving',
  SAVED = 'saved',
  CLOSE = 'close',
  CLOSED = 'closed',
  ERROR = 'error',
  FAILED = 'failed'
}

interface TargetColumn {
  field: string
  header: string
  isRequired: boolean
}

interface UploadStatus {
  id: number
  form: any
  user: any
  send_upload_email: boolean
  send_email: boolean
  send_whatsapp: boolean
  send_sms: boolean
  check_email: boolean
  replace_barcode: boolean
  force_approval: boolean
  register_dtcm: boolean
  progress?: number
  status: {
    state: UploadState
    close_time?: string
    closed_time?: string
    compile_start?: string
    recordCount: number
    compile_last_update?: string
    compiledCount?: number
    record_compile_avg?: number
    estimated_compile_time?: number
    estimated_compile_end?: string
    est_compile_remaining_time?: number
    compile_end?: string
    save_start?: string
    save_last_update?: string
    batch_no?: string
    savedCount?: number
    estimated_save_time?: number
    estimated_save_end?: string
    est_save_remaining_time?: number
    save_end?: string
  }
  validLinesCount: number
  invalidLinesCount: number
  savedLinesCount: number
  extractedLinesCount: number
  created_at: string
}

interface UploadHistoryResponse {
  count: number
  uploads: UploadStatus[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// Define supported tab views
enum TabView {
  IMPORT_WIZARD = 'import_wizard',
  UPLOAD_HISTORY = 'upload_history',
  UPLOAD_PROGRESS = 'upload_progress'
}

interface UploadStateInfo {
  isUploaded: boolean
  isCompiling: boolean
  isCompiled: boolean
  isSaving: boolean
  isSaved: boolean
  isClose: boolean
  isStopped: boolean
  isInProgress: boolean
}

interface UploadStatusInfo {
  progress: number
  statusLabel: string
  statusClass: string
}

type ImportFlowStep = 'review' | 'topup'

@Component({
  selector: 'ngx-import-registrations',
  templateUrl: './import-registrations.component.html',
  styleUrls: ['./import-registrations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    MatIconModule,
    NbCheckboxModule,
    NbTooltipModule,
    WalletTopUpModalComponent,
    CurrencyPipe,
    InfiniteScrollModule
  ],
  standalone: true
})
export class ImportRegistrationsComponent implements OnInit, OnDestroy {
  @Input() registrationsDataSourceCollection: RegistrationsDataSourceCollection
  @Input() dialogRef: any
  @Output() downloadTemplateEvent = new EventEmitter<void>()
  @Output() fileChangeEvent = new EventEmitter<File>()
  @Output() uploadEvent = new EventEmitter<any>()

  public isDuplicateHeaders = false
  public enable_communication: boolean = false

  public readonly currentPersonaForm = signal<Persona | undefined>(undefined)

  public readonly isMemberImport = computed(() => this.currentPersonaForm()?.type === PersonaTypes.MEMBER)

  public readonly parentPersona = computed((): Persona | null => {
    const parentId = this.currentPersonaForm()?.parent
    if (!parentId || !this.registrationsDataSourceCollection) return null
    const match = this.registrationsDataSourceCollection.registrationsDataSources.find((source) => {
      const form = source.registrationsDataSource.form
      return String(form.id) === String(parentId) && form.type === PersonaTypes.GROUP
    })
    return match?.registrationsDataSource.form ?? null
  })

  public readonly missingParentForm = computed(() => this.isMemberImport() && !this.parentPersona()?.slug)

  public readonly fileStep = computed(() => (this.isMemberImport() ? 2 : 1))

  public readonly mapStep = computed(() => (this.isMemberImport() ? 3 : 2))

  public readonly reviewStep = computed(() => (this.isMemberImport() ? 4 : 3))

  /**
   * Whether WhatsApp / SMS import confirmations can be offered (persona has a phone-number field).
   * @returns true when the current form includes PersonaFieldTypes.PHONE
   */
  get canSendPhoneConfirmations(): boolean {
    const selectedIndex = this.registrationsDataSourceCollection?.selectedIndex || 0
    const currentPersona = this.registrationsDataSourceCollection?.registrationsDataSources?.[selectedIndex]
    return formHasPhoneNumberElement(currentPersona?.registrationsDataSource?.form)
  }

  // Tab management
  TabView = TabView // Expose enum to template
  activeTab: TabView = TabView.IMPORT_WIZARD
  isStoppedByUser = false
  // Wizard steps. Member imports insert a parent-picker step before the file step.
  currentStep = 1
  totalSteps = 3

  public readonly parentResults = signal<ParentRegistrationOption[]>([])
  public readonly parentSearchTerm = signal('')
  public readonly parentSearchLoading = signal(false)
  public readonly parentSearchLoadingMore = signal(false)
  public readonly parentSearchError = signal('')
  public readonly parentHasMore = signal(false)
  private parentPage = 1
  private readonly parentPerPage = 20
  public readonly selectedParent = signal<ParentRegistrationOption | null>(null)
  /** True when a superadmin asked to skip email check and the API kept it on. */
  public readonly emailCheckForcedOn = signal(false)
  private requestedSkipEmailCheck = false
  /** Upload id started by import-members. History views keep using /upload/{id}. */
  private memberImportUploadId: number | null = null
  private parentSearch$ = new Subject<string>()

  public readonly parentInfiniteScrollDisabled = computed(() => !this.parentHasMore() || this.parentSearchLoading() || this.parentSearchLoadingMore())

  // Forms
  fileForm: UntypedFormGroup
  mappingForm: UntypedFormGroup
  confirmationForm: UntypedFormGroup

  // File data
  file: File | null = null
  fileData: any[] = []
  fileColumns: string[] = []
  isDragging = false

  // Target columns from the system
  personaColumns: TargetColumn[] = []

  // Column mappings
  columnsMap: ColumnMapping[] = []

  // Final mapped data
  mappedData: any[] = []

  // Preview data
  fileRows: any[] = []

  // Supported file types
  supportedFormats = '.csv'

  // Added for the new onFileChange method
  fileName = ''

  // Handle for current dialog instance
  private thisDialogRef: NbDialogRef<any> | null = null

  // Upload history and progress tracking
  currentUpload: UploadStatus | null = null
  uploadHistory: UploadStatus[] = []
  lastUploadId: string | null = null
  uploadProgress = 0
  isUploadInProgress = false

  // Destroy notifier for unsubscribing from observables
  private destroy$ = new Subject<void>()

  // Add cache for available options
  private optionsCache = new Map<string, string[]>()

  // Add property to track the upload status subscription
  private uploadStatusSubscription: any

  // Pagination state
  current_page = 1
  per_page = 10
  last_page = 0
  total = 0
  isLoadingMore = false

  smsPrice: number = 0
  whatsappPrice: number = 0
  emailPrice: number = 0

  /** Inline flow step on review: summary or wallet top-up. */
  public flowStep: ImportFlowStep = 'review'
  loadingBalance = false
  loadingPaymentMethods = false
  insufficientBalance = false
  balanceChecked = false
  requiredAmountUsd = 0
  currentBalanceUsd = 0
  paymentMethods: BillingPaymentMethod[] = []
  isImporting = false
  private unlistenLayeredOverlayDismiss?: () => void

  /** Monotonic id so stale balance API responses are ignored after leaving review. */
  private _balanceCheckSeq = 0

  // Add selectedUpload property
  selectedUpload: UploadStatus | null = null

  // Add property to track previous progress
  private previousProgress = 0

  constructor(
    private fb: UntypedFormBuilder,
    private zone: NgZone,
    private toastrService: NbToastrService,
    private databaseService: DatabaseService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private _billingService: BillingService,
    private navbarService: NavbarService,
    private _organizationService: OrganizationService
  ) {
    // Initialize forms immediately to avoid null references
    this.initForms()
  }

  private _syncCurrentPersonaForm(): void {
    const selectedIndex = this.registrationsDataSourceCollection?.selectedIndex || 0
    this.currentPersonaForm.set(this.registrationsDataSourceCollection?.registrationsDataSources?.[selectedIndex]?.registrationsDataSource?.form)
  }

  /** Member imports are platform-admin only and add a parent-search step before the file. */
  ngOnInit(): void {
    this._syncCurrentPersonaForm()
    if (this.isMemberImport() && !this.isSuperAdmin) {
      this.toastrService.danger('Only platform admins can import members onto a group parent', 'Import unavailable')
      // dialogRef is assigned by the opener after open(), so close on the next tick
      setTimeout(() => this.closeAndCleanupCompletely())
      return
    }

    // Ensure target columns are loaded
    this.loadTargetColumns()
    this.enable_communication = this.getUploadParameters().enable_communication
    // Check if we're in a Nebular dialog
    if (window && (window as any).nb && (window as any).nb.dialogRef) {
      this.thisDialogRef = (window as any).nb.dialogRef
    }
    const comunicationPrice = this.authService.importConfirmationUnitPriceUsd
    this.smsPrice = comunicationPrice.sms
    this.whatsappPrice = comunicationPrice.whatsapp
    this.emailPrice = comunicationPrice.email

    if (this.isMemberImport()) {
      this.totalSteps = 4
      if (!this.missingParentForm()) this.searchParents('')
    }

    this.parentSearch$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((term) => this.searchParents(term))

    this.confirmationForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isMemberImport() && this.currentStep === this.reviewStep() && this.flowStep === 'review') {
        this.checkImportWalletBalance()
      }
    })
    this.unlistenLayeredOverlayDismiss = listenLayeredOverlayDismiss({
      closeParent: () => this.closeAndCleanupCompletely()
    })

    // Check for existing uploads in progress
    // this.checkExistingUploads()
  }

  ngOnDestroy(): void {
    this.unlistenLayeredOverlayDismiss?.()
    // Unsubscribe from all observables
    this.destroy$.next()
    this.destroy$.complete()

    // Clean up upload status subscription
    if (this.uploadStatusSubscription) {
      this.uploadStatusSubscription.unsubscribe()
      this.uploadStatusSubscription = null
    }

    this._reset()
  }

  /**
   * Set the current upload and update related state
   * This should only be called in three scenarios:
   * 1. When user uploads a file themselves (handleUploadSuccess)
   * 2. When user clicks on a specific row (viewUploadDetails)
   * 3. When there's an actual upload running in background (checkExistingUploads)
   */
  private setCurrentUpload(upload: UploadStatus | null, source: 'upload' | 'view' | 'background'): void {
    // Only set currentUpload if:
    // 1. This is a new upload from the user (source === 'upload')
    // 2. User explicitly clicked to view details (source === 'view')
    // 3. There's an actual upload running in background (source === 'background')
    if (upload && (source === 'upload' || source === 'view' || (source === 'background' && this.getUploadState(upload).isInProgress))) {
      this.currentUpload = upload
      this.lastUploadId = upload.id.toString()
      const statusInfo = this.getUploadStatusInfo(upload)
      this.uploadProgress = statusInfo.progress
      this.isUploadInProgress = !this.getUploadState(upload).isSaved

      // Also update selectedUpload if this is an active upload
      if (this.getUploadState(upload).isInProgress) {
        this.selectedUpload = upload
      }
    } else {
      // Only reset if we're not in one of the allowed scenarios
      if (!upload || source !== 'background') {
        this.currentUpload = null
        this.lastUploadId = null
        this.uploadProgress = 0
        this.isUploadInProgress = false
      }
    }
    this.cdr.detectChanges()
  }

  /**
   * Check if there are any uploads in progress
   */
  checkExistingUploads(): void {
    if (!this.registrationsDataSourceCollection) return

    try {
      const currentPersona = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex]
      const eventSlug = this.registrationsDataSourceCollection.registrationsDataSources[0].registrationsDataSource.initializeRequest.event
      const formSlug = currentPersona.slug
      const workspace = this.databaseService.getWorkspaceSlug()

      this.databaseService
        .getUploadHistory(workspace, eventSlug, formSlug)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: UploadHistoryResponse) => {
            // Sort uploads by created_at date, newest first
            this.uploadHistory = (response.uploads || []).sort((a, b) => {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            })

            // Check if there's an ongoing upload
            const inProgressUploads = this.uploadHistory.filter((upload) => {
              return this.getUploadState(upload).isInProgress
            })

            if (inProgressUploads.length > 0) {
              // Set currentUpload with 'background' source for running uploads
              this.setCurrentUpload(inProgressUploads[0], 'background')
              // Only switch to upload progress if upload is in progress
              if (this.isUploadInProgress) {
                this.activeTab = TabView.UPLOAD_PROGRESS
              }
            } else {
              this.setCurrentUpload(null, 'background')
            }
          },
          (error) => {
            console.error('Error fetching upload history:', error)
            this.setCurrentUpload(null, 'background')
          }
        )
    } catch (error) {
      console.error('Error checking existing uploads:', error)
      this.setCurrentUpload(null, 'background')
    }
  }

  /**
   * Calculate progress for an upload
   */
  calculateUploadProgress(upload: UploadStatus): number {
    return upload.progress || 0
  }

  /**
   * Handle upload status error
   */
  private handleUploadStatusError(error: any): void {
    console.error('Error updating upload progress:', error)
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return ''

    try {
      // Replace space with 'T' and add 'Z' to indicate it's UTC
      const isoUtcString = dateString.replace(' ', 'T') + 'Z'

      const date = new Date(isoUtcString) // Now treated as UTC

      return date.toLocaleString(undefined, {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    } catch (e) {
      return dateString
    }
  }

  /**
   * Format full date for tooltip
   */
  formatFullDate(dateString: string): string {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)
      // Use a more detailed format for tooltips
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      })
    } catch (e) {
      return dateString
    }
  }

  /**
   * Switch to a specific tab - never override user's choice
   */
  switchTab(tab: TabView): void {
    this.activeTab = tab
    // Reset selectedUpload when changing tabs
    this.selectedUpload = null

    if (tab === TabView.IMPORT_WIZARD) {
      // Reset the wizard state for a new import
      this.resetWizard()
      if (this.getUploadState(this.currentUpload).isSaved || this.getUploadState(this.currentUpload).isStopped) {
        this._reset()
      }
      this.currentStep = 1
    }
    // If switching to history tab, refresh the history
    if (tab === TabView.UPLOAD_HISTORY) {
      this.refreshUploadHistory()
    }
  }

  initForms(): void {
    this.fileForm = this.fb.group({
      fileInput: [null, Validators.required]
    })

    this.mappingForm = this.fb.group({})

    this.confirmationForm = this.fb.group({
      sendConfirmationEmail: [false],
      sendWhatsappConfirmation: [false],
      sendSmsConfirmation: [false],
      bypassEmailValidation: [false]
    })
  }

  /**
   * Platform / super admin (user.is_admin). Used to gate import-only controls.
   */
  get isSuperAdmin(): boolean {
    return this.authService.isAdmin
  }

  /**
   * Reset the wizard to its initial state for a new import.
   * Also clears the member-import upload id and the forced email-check flag.
   */
  resetWizard(): void {
    // Reset step
    this.currentStep = 1

    // Reset forms to initial state
    this.initForms()

    // Reset file data
    this.file = null
    this.fileData = []
    this.fileColumns = []
    this.fileRows = []
    this.fileName = ''
    this.isDragging = false
    this.isDuplicateHeaders = false

    // Reset column mappings
    this.columnsMap = []
    this.mappedData = []

    // Reset upload tracking
    this.currentUpload = null
    this.isUploadInProgress = false
    this.uploadProgress = 0
    this.previousProgress = 0
    this.memberImportUploadId = null
    this.emailCheckForcedOn.set(false)
    this.requestedSkipEmailCheck = false

    // Clear options cache
    this.optionsCache.clear()

    // Trigger change detection
    this.cdr.detectChanges()
  }

  loadTargetColumns(): void {
    if (!this.registrationsDataSourceCollection) {
      console.error('No registrations data source collection available')
      return
    }

    try {
      // Get the current persona
      const selectedIndex = this.registrationsDataSourceCollection.selectedIndex || 0
      const currentPersona = this.registrationsDataSourceCollection.registrationsDataSources[selectedIndex]

      // Initialize empty array
      this.personaColumns = []

      // Access the form directly from the data source which should have the form info
      if (currentPersona && currentPersona.registrationsDataSource) {
        const formElements = currentPersona.registrationsDataSource.form?.form_elements || []

        if (formElements && formElements.length > 0) {
          formElements.forEach((element) => {
            // Skip internal fields
            if (element.name && !element.name.startsWith('_')) {
              this.personaColumns.push({
                field: element.name,
                header: element.label || this.formatColumnName(element.name),
                isRequired: Boolean(element.is_required)
              })
            }
          })
        }

        // If we couldn't get form elements, try another approach using basic fields
        if (this.personaColumns.length === 0) {
          // Look for fields in the form's schema or properties
          const formSchema = currentPersona.registrationsDataSource.form

          if (formSchema) {
            // Try to extract fields from common properties
            ;['full_name', 'email', 'phone'].forEach((fieldName) => {
              this.personaColumns.push({
                field: fieldName,
                header: this.formatColumnName(fieldName),
                isRequired: false
              })
            })
          }
        }
      }

      // If we still don't have columns, log a warning
      if (this.personaColumns.length === 0) {
        console.warn('Could not find any fields in the form. Using standard fields.')
        // Fallback to minimum set
        this.personaColumns = [
          { field: 'full_name', header: 'Full name', isRequired: false },
          { field: 'email', header: 'E-mail', isRequired: false },
          { field: 'phone', header: 'Phone number', isRequired: false }
        ]
      }
    } catch (error) {
      // Fallback to basic fields
      this.personaColumns = [
        { field: 'full_name', header: 'Full name', isRequired: false },
        { field: 'email', header: 'E-mail', isRequired: false },
        { field: 'phone', header: 'Phone number', isRequired: false }
      ]
    }
  }

  formatColumnName(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  // File drag-and-drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = true
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false
  }

  onDrop(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false

    if (event.dataTransfer && event.dataTransfer.files.length) {
      const file = event.dataTransfer.files[0]
      // Reset the file input to ensure change event triggers next time
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      } else {
        console.warn('File input element not found')
      }

      // Check if file type is supported
      const isSupported = this.isFileTypeSupported(file)

      if (isSupported) {
        this.fileForm.get('fileInput')?.setErrors(null)
        this.handleFileSelection(file)
      } else {
        this.fileForm.get('fileInput')?.setErrors({ invalidFileType: true })
        this.file = null
      }
    } else {
      console.warn('No files found in drop event')
    }
  }

  isFileTypeSupported(file: File): boolean {
    const fileName = file.name.toLowerCase()
    const formats = this.supportedFormats.split(',').map((format) => format.trim().toLowerCase())
    const isSupported = formats.some((format) => fileName.endsWith(format))
    return isSupported
  }

  removeFile(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this._reset()
    // Reset the file input to ensure change event triggers next time
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  private _reset() {
    this.isStoppedByUser = false
    this.file = null
    this.fileData = []
    this.fileColumns = []
    this.fileRows = []
    this.isDuplicateHeaders = false
    this.resetProgress()
    this.fileForm.reset()
    // Reset the file input element value to ensure change event triggers next time
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  getFileIcon(): string {
    return !this.file ? 'file-outline' : 'list-outline'
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  getTargetColumnHeader(field: string): string {
    const target = this.personaColumns.find((t) => t.field === field)
    return target ? target.header : field
  }

  onFileChange(files: FileList): void {
    if (files && files.length > 0) {
      const file = files[0]
      // Check if file type is supported
      const isSupported = this.isFileTypeSupported(file)
      if (isSupported) {
        this.fileForm.get('fileInput')?.setErrors(null)
        this.handleFileSelection(file)
      } else {
        this.fileForm.get('fileInput')?.setErrors({ invalidFileType: true })
        this.file = null
      }
    } else {
      this.fileForm.get('fileInput')?.setErrors({ invalidFileType: true })
      this.file = null
    }
  }

  handleFileSelection(file: File): void {
    // Set the file object
    this.file = file
    // Update form control value (don't set the input element value directly)
    this.fileForm.get('fileInput')?.setValue(file)
    this.fileForm.get('fileInput')?.updateValueAndValidity()

    // Display file name
    this.fileName = file.name

    // Read the file data
    this.readFileData(file)

    // Emit the file change event
    this.fileChangeEvent.emit(file)
  }

  readFileData(file: File): void {
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target && e.target.result) {
        try {
          // Parse the actual file content
          this.parseFileData(e.target.result)
        } catch (error) {
          console.error('Error reading file data:', error)
          alert('Error reading file. Please ensure it is a valid Excel or CSV file.')
        }
      }
    }
    reader.readAsArrayBuffer(file)
  }

  parseFileData(data: ArrayBuffer | string): void {
    try {
      // Convert ArrayBuffer to string if needed
      const csvText = typeof data === 'string' ? data : new TextDecoder().decode(data)

      // Normalize line endings to \n and remove stray \r
      const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

      // Split into lines and remove empty lines
      const lines = normalizedText.split('\n').filter((line) => line.trim())

      // Parse headers from first line
      const headers = lines[0].split(',').map((h) => this.sanitizeColumnName(h))

      // Check for duplicate headers
      const uniqueHeaders = new Set(headers)
      if (uniqueHeaders.size !== headers.length) {
        this.isDuplicateHeaders = true
      }

      // Store headers
      this.fileColumns = headers

      // Parse data rows
      this.fileData = lines.slice(1).map((line) => {
        const values = line.split(',').map((value) => value.trim())
        const row: Record<string, any> = {}

        headers.forEach((header, index) => {
          row[header] = values[index] || ''
        })

        return row
      })

      // For preview, take first few rows
      this.fileRows = this.fileData.slice(0, 3)

      // Initialize column mappings based on the file data
      this.initColumnMappings()

      // Auto-map columns that match by name
      this.autoMapColumns()
    } catch (error) {
      console.error('Error parsing CSV file:', error)
      throw new Error('Error parsing CSV file. Please ensure it is a valid CSV file.')
    }
  }

  autoMapColumns(): void {
    // Try to automatically map columns that have similar names
    this.fileColumns.forEach((sourceCol) => {
      let matchingTarget = this.personaColumns.find((target) => target.field.toLowerCase() === sourceCol.toLowerCase() || target.header.toLowerCase() === sourceCol.toLowerCase())
      if (!matchingTarget) {
        if (sourceCol.toLowerCase().includes('full') && sourceCol.toLowerCase().includes('name')) {
          matchingTarget = this.personaColumns.find((t) => t.field === 'full_name')
        } else if (sourceCol.toLowerCase().includes('first') && sourceCol.toLowerCase().includes('name')) {
          matchingTarget = this.personaColumns.find((t) => t.field === 'first_name')
        } else if (sourceCol.toLowerCase().includes('last') && sourceCol.toLowerCase().includes('name')) {
          matchingTarget = this.personaColumns.find((t) => t.field === 'last_name')
        } else if (sourceCol.toLowerCase().includes('email') || sourceCol.toLowerCase().includes('mail')) {
          matchingTarget = this.personaColumns.find((t) => t.field === 'email')
        } else if (sourceCol.toLowerCase().includes('phone')) {
          matchingTarget = this.personaColumns.find((t) => t.field === 'phone' || t.field === 'phone_number')
        }
      }
      if (matchingTarget) {
        const control = this.mappingForm.get(matchingTarget.field)
        if (control) {
          control.setValue(sourceCol)
          control.markAsTouched()
          control.markAsDirty()
        }
        const mapping = this.columnsMap.find((m) => m.targetColumn === matchingTarget.field)
        if (mapping) {
          mapping.sourceColumn = sourceCol
        }
      }
    })
  }

  initColumnMappings(): void {
    // Reset mappings
    this.columnsMap = []
    this.optionsCache.clear()

    // Create a new form group for mappings
    const mappingControls: { [key: string]: UntypedFormControl } = {}

    // Create form controls for each target column
    this.personaColumns.forEach((targetCol) => {
      // Create control first
      mappingControls[targetCol.field] = new UntypedFormControl('')

      // Add to column mappings
      this.columnsMap.push({
        sourceColumn: '',
        targetColumn: targetCol.field,
        isRequired: targetCol.isRequired
      })
    })

    // Create the form group with all controls prepared
    this.mappingForm = this.fb.group(mappingControls)

    // Force change detection
    this.cdr.detectChanges()
  }

  /**
   * Gets the list of available source columns for a target column's dropdown
   * This ensures that once a source column is selected in one dropdown, it won't appear in others
   * @param targetField The target field we're getting options for
   * @returns List of available source columns that haven't been selected elsewhere
   */
  getAvailableSourceColumns(targetField: string): string[] {
    // Check cache first
    if (this.optionsCache.has(targetField)) {
      const cachedOptions = this.optionsCache.get(targetField)
      if (cachedOptions) {
        return cachedOptions
      }
    }

    // Get all source columns that are already selected in other dropdowns
    const alreadySelectedColumns = this.columnsMap
      .filter((mapping) => {
        // Skip the current column we're getting options for
        const isNotCurrentColumn = mapping.targetColumn !== targetField
        // Only include columns that have a selection
        const hasSelection = mapping.sourceColumn !== ''
        return isNotCurrentColumn && hasSelection
      })
      .map((mapping) => mapping.sourceColumn)

    // Return all file columns except those that are already selected
    const availableOptions = this.fileColumns.filter((column) => !alreadySelectedColumns.includes(column))

    // Cache the result
    this.optionsCache.set(targetField, availableOptions)

    return availableOptions
  }

  /**
   * Get the mapped source column for a target field
   */
  getMappedSourceColumn(targetField: string): string {
    const mapping = this.columnsMap.find((m) => m.targetColumn === targetField)
    return mapping ? mapping.sourceColumn : ''
  }

  updateMapping(sourceColumn: string, targetColumn: string): void {
    // Update the mapping
    const mapping = this.columnsMap.find((m) => m.targetColumn === targetColumn)
    if (mapping) {
      mapping.sourceColumn = this.sanitizeColumnName(sourceColumn)
    }
    // Also update the form control value and mark as touched/dirty
    const control = this.mappingForm.get(targetColumn)
    if (control) {
      control.setValue(this.sanitizeColumnName(sourceColumn))
      control.markAsTouched()
      control.markAsDirty()
    }
    this.optionsCache.clear()
    this.cdr.detectChanges()
  }

  mapData(): void {
    this.mappedData = this.fileData.map((row) => {
      const mappedRow: Record<string, any> = {}

      this.columnsMap.forEach((mapping) => {
        if (mapping.sourceColumn && row[mapping.sourceColumn] !== undefined) {
          mappedRow[mapping.targetColumn] = row[mapping.sourceColumn]
        }
      })

      return mappedRow
    })

    // Move to next step
    this.goToNextStep()
  }

  /** Member imports skip the wallet check on review because they send no confirmations. */
  goToNextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++
      if (this.currentStep === this.reviewStep()) {
        this.flowStep = 'review'
        if (!this.isMemberImport()) this.checkImportWalletBalance()
      }
    }
  }

  /** Review is reviewStep so the extra parent step on a member import is included. */
  goToPreviousStep(): void {
    if (this.flowStep === 'topup') {
      this.backFromTopUp()
      return
    }
    if (this.currentStep > 1) {
      if (this.currentStep === this.reviewStep()) {
        this._resetImportReviewState()
      }
      this.currentStep--
    }
  }

  /**
   * Clear attendee communication choices and wallet balance state when leaving the review step.
   */
  private _resetImportReviewState(): void {
    this._balanceCheckSeq++
    this.confirmationForm.reset(
      {
        sendConfirmationEmail: false,
        sendWhatsappConfirmation: false,
        sendSmsConfirmation: false,
        bypassEmailValidation: false
      },
      { emitEvent: false }
    )
    this.flowStep = 'review'
    this.loadingBalance = false
    this.loadingPaymentMethods = false
    this.insufficientBalance = false
    this.balanceChecked = false
    this.requiredAmountUsd = 0
    this.currentBalanceUsd = 0
    this.cdr.markForCheck()
  }

  /**
   * Total communication cost for selected channels × records to import.
   */
  public communicationCostUsd(): number {
    if (!this.enable_communication || this.mappedData.length === 0) return 0

    const count = this.mappedData.length
    let total = 0
    if (this.confirmationForm.get('sendConfirmationEmail')?.value) total += count * this.emailPrice
    if (this.canSendPhoneConfirmations && this.confirmationForm.get('sendWhatsappConfirmation')?.value) total += count * this.whatsappPrice
    if (this.canSendPhoneConfirmations && this.confirmationForm.get('sendSmsConfirmation')?.value) total += count * this.smsPrice
    return roundMoney(total)
  }

  /**
   * Amount still needed in the wallet to cover import communications.
   */
  balanceShortfallUsd(): number {
    return Math.max(0, roundMoney(this.requiredAmountUsd - this.currentBalanceUsd))
  }

  /**
   * Suggested top-up for the wallet: shortfall floored at $50.
   */
  suggestedTopUpAmount(): number {
    return Math.max(50, this.balanceShortfallUsd())
  }

  /**
   * Fetch wallet balance and compare it to the import communication total.
   * @param {number} [optimisticBalanceUsd] Known balance to apply immediately (e.g. after top-up).
   */
  checkImportWalletBalance(optimisticBalanceUsd?: number): void {
    const total = this.communicationCostUsd()
    if (total <= 0 || !this._billingService.workspaceId) {
      this.loadingBalance = false
      this.insufficientBalance = false
      this.balanceChecked = true
      this.requiredAmountUsd = 0
      this.cdr.markForCheck()
      return
    }

    const checkSeq = ++this._balanceCheckSeq

    if (optimisticBalanceUsd !== undefined) {
      this._applyImportBalanceCheck(optimisticBalanceUsd, total)
    } else {
      this.loadingBalance = true
      this.insufficientBalance = false
      this.balanceChecked = false
    }

    const workspaceId = String(this._billingService.workspaceId)
    this.navbarService
      .getBalance(workspaceId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (balance) => {
          if (checkSeq !== this._balanceCheckSeq) return
          const resolvedBalance = optimisticBalanceUsd !== undefined ? Math.max(balance, optimisticBalanceUsd) : balance
          this._applyImportBalanceCheck(resolvedBalance, total)
        },
        error: () => {
          if (checkSeq !== this._balanceCheckSeq) return
          this.loadingBalance = false
          this.balanceChecked = false
          this.toastrService.danger('Could not verify wallet balance. Please try again.', 'Error')
          this.cdr.markForCheck()
        }
      })
  }

  /**
   * Update balance state and whether the wallet can cover import communications.
   * @param {number} balance Current wallet balance in USD
   * @param {number} total Required total in USD
   */
  private _applyImportBalanceCheck(balance: number, total: number): void {
    this.loadingBalance = false
    this.balanceChecked = true
    this.currentBalanceUsd = roundMoney(balance)
    this.requiredAmountUsd = roundMoney(total)
    const shortfall = roundMoney(total - this.currentBalanceUsd)
    this.insufficientBalance = shortfall > 0
    this.cdr.markForCheck()
  }

  /**
   * Load payment methods and navigate to the inline top-up step.
   */
  goToTopUpStep(): void {
    if (this.loadingPaymentMethods) return

    this.loadingPaymentMethods = true
    this._organizationService
      .getOrganizationPaymentMethods(this._billingService.workspaceId)
      .pipe(
        catchError(() => of([] as BillingPaymentMethod[])),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((methods) => {
        this.paymentMethods = methods
        this.loadingPaymentMethods = false
        this.flowStep = 'topup'
        this.cdr.markForCheck()
      })
  }

  /**
   * Return from top-up to the review step and clear communication/balance state.
   */
  backFromTopUp(): void {
    this._resetImportReviewState()
  }

  /**
   * After a successful top-up, return to review and re-check wallet balance.
   */
  public onWalletTopUpSuccess(amount: number): void {
    if (!this._billingService.workspaceId) return

    const updatedBalance = roundMoney(this.currentBalanceUsd + amount)
    this._billingService.publishWalletBalance(updatedBalance)
    this.flowStep = 'review'
    this.checkImportWalletBalance(updatedBalance)
  }

  /**
   * Whether the import CTA should use the top-up (primary/blue) style instead of import (success/green).
   */
  isImportTopUpAction(): boolean {
    return this.insufficientBalance && this.communicationCostUsd() > 0 && !this.isImporting
  }

  /**
   * Whether the import action can proceed (balance OK or no comms cost).
   */
  canImportProceed(): boolean {
    if (this.hasNoMappedColumns()) return false
    const cost = this.communicationCostUsd()
    if (cost <= 0) return true
    if (this.insufficientBalance) return true
    return this.balanceChecked
  }

  /** On a member import, the first step stays disabled until a parent is selected. */
  isNextButtonDisabled(): boolean {
    if (this.isMemberImport() && this.currentStep === 1) {
      return this.missingParentForm() || !this.selectedParent()
    }
    if (this.currentStep === this.fileStep()) {
      if (!this.file) return true
      else if (this.fileRows.length === 0) return true
      else if (this.isDuplicateHeaders) return true
    } else if (this.currentStep === this.mapStep()) {
      // Check if all required target columns are mapped
      const allRequiredMapped = this.personaColumns
        .filter((col) => col.isRequired)
        .every((requiredField) => {
          const mappedSource = this.getMappedSourceColumn(requiredField.field)
          return mappedSource && mappedSource.length > 0
        })
      return !allRequiredMapped
    }
    return false
  }

  /** Column mapping runs on mapStep, which shifts when a member import adds the parent step. */
  onNextButtonClick(): void {
    if (this.currentStep === this.mapStep()) {
      this.mapData()
    } else {
      this.goToNextStep()
    }
  }

  /** Debounces parent search while the user types a name or email. */
  onParentSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? ''
    this.parentSearchTerm.set(value)
    this.parentSearch$.next(value)
  }

  /** Stores the group registration these members will be imported under. */
  selectParent(parent: ParentRegistrationOption): void {
    this.selectedParent.set(parent)
    this.parentSearchError.set('')
  }

  /** Label for a parent row: name, otherwise organization. */
  parentDisplayName(parent: ParentRegistrationOption): string {
    return parent.name || parent.organization || 'Untitled'
  }

  /** Secondary line: email and how many members the parent already has. */
  parentDisplaySubtitle(parent: ParentRegistrationOption): string {
    const parts = [parent.email, `${parent.childCount} members`].filter(Boolean)
    return parts.join(' · ')
  }

  /** Loads the next page of parent registrations when the list scrolls. */
  onParentListScroll(): void {
    if (this.parentInfiniteScrollDisabled()) return
    this.searchParents(this.parentSearchTerm(), true)
  }

  /** Fallback when ngx-infinite-scroll misses nested dialog scroll events. */
  onParentListNativeScroll(event: Event): void {
    const el = event.target as HTMLElement | null
    if (!el || this.parentInfiniteScrollDisabled()) return
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
    if (remaining < 80) this.onParentListScroll()
  }

  /** Searches group registrations that can receive imported members. */
  searchParents(keyword: string, loadMore = false): void {
    if (!this.isMemberImport() || this.missingParentForm()) return
    if (loadMore && this.parentInfiniteScrollDisabled()) return

    const { eventSlug } = this.getUploadParameters()
    const parentSlug = this.parentPersona()?.slug
    if (!parentSlug) return

    if (!loadMore) {
      this.parentPage = 1
      this.parentHasMore.set(false)
      this.parentResults.set([])
      this.parentSearchLoading.set(true)
    } else {
      this.parentSearchLoadingMore.set(true)
    }
    this.parentSearchError.set('')

    const page = loadMore ? this.parentPage + 1 : 1
    this.databaseService
      .searchParentRegistrations(eventSlug, parentSlug, keyword, page, this.parentPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const existingIds = new Set(this.parentResults().map((row) => row.id))
          const nextItems = result.items.filter((row) => !existingIds.has(row.id))
          this.parentResults.set(loadMore ? [...this.parentResults(), ...nextItems] : result.items)
          this.parentPage = result.currentPage || page
          this.parentHasMore.set(this.resolveParentHasMore(result))
          this.parentSearchLoading.set(false)
          this.parentSearchLoadingMore.set(false)
        },
        error: (error) => {
          this.parentSearchLoading.set(false)
          this.parentSearchLoadingMore.set(false)
          this.parentSearchError.set(this.extractErrorMessage(error))
        }
      })
  }

  /** True when another parent-search page is still available. */
  private resolveParentHasMore(result: { currentPage: number; lastPage: number; total: number }): boolean {
    if (result.lastPage > 0) return this.parentPage < result.lastPage
    if (result.total > 0) return this.parentResults().length < result.total
    return false
  }

  getMappedColumnsCount(): number {
    return this.columnsMap.filter((m) => m.sourceColumn && m.sourceColumn.trim() !== '').length
  }

  hasNoMappedColumns(): boolean {
    return !this.columnsMap.some((m) => m.targetColumn)
  }

  // Helper method to safely get form control
  safelyGetControl(controlName: string): UntypedFormControl {
    if (!this.mappingForm) {
      return new UntypedFormControl('')
    }

    const control = this.mappingForm.get(controlName)
    if (control) {
      return control as UntypedFormControl
    }

    // If control doesn't exist, add it to the form
    const newControl = new UntypedFormControl('')
    this.mappingForm.addControl(controlName, newControl)
    return newControl
  }

  setCheckboxValue(value: boolean, field: string): void {
    this.confirmationForm.get(field)?.setValue(value)
  }

  downloadTemplate(): void {
    this.downloadTemplateEvent.emit()
  }

  /**
   * Gets the form name for an upload record
   * @param upload The upload status object
   * @returns The form name or a default value
   */
  getFormNameForUpload(upload: UploadStatus): string {
    if (!upload || !upload.form) {
      return 'Unknown Form'
    }

    if (typeof upload.form === 'string') {
      return upload.form
    }

    if (upload.form && upload.form.title) {
      return upload.form.title
    }

    if (upload.form && upload.form.name) {
      return upload.form.name
    }

    // If nothing else works, try to get form title from the current selected data source
    if (this.registrationsDataSourceCollection) {
      const currentPersona = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex]
      if (currentPersona && currentPersona.title) {
        return currentPersona.title
      }
    }

    return 'Registration Form'
  }

  // Simple method to close the dialog using the provided DialogRef
  public closeAndCleanupCompletely(): void {
    if (this.dialogRef) {
      this.dialogRef.close()
    }
  }

  /**
   * Check if upload should show send email button
   * @param upload The upload status object
   * @returns Boolean indicating if send email button should be shown
   */
  shouldShowSendEmailButton(upload: UploadStatus): boolean {
    if (upload.send_email) {
      return false
    }

    const state = this.getUploadState(upload)
    const hasInvalidOnly = upload.invalidLinesCount > 0 || upload.savedLinesCount === 0

    if (hasInvalidOnly) return false

    const case1 = !this.isStoppedByUser && (state.isSaved || state.isStopped)
    const case2 = this.isStoppedByUser && (state.isClose || state.isInProgress)

    return case1 || case2
  }

  /**
   * Check if upload should show download invalid records button
   * @param upload The upload status object
   * @returns Boolean indicating if download invalid records button should be shown
   */
  hasInvalidRecords(upload: UploadStatus): boolean {
    return (this.getUploadState(upload).isSaved || this.getUploadState(upload).isStopped) && upload.invalidLinesCount > 0
  }

  /** A member import failed when the API reports error or failed, or closed without the user stopping it. */
  isImportFailed(upload: UploadStatus | null): boolean {
    const state = upload?.status?.state
    if (state === UploadState.ERROR || state === UploadState.FAILED) return true
    return state === UploadState.CLOSED && !this.isStoppedByUser
  }

  /**
   * Main upload method — checks wallet balance for communications before importing.
   */
  upload(): void {
    const cost = this.communicationCostUsd()
    if (cost > 0) {
      if (this.insufficientBalance) {
        this.goToTopUpStep()
        return
      }
      if (this.loadingBalance) return
      if (!this.balanceChecked) {
        this.checkImportWalletBalance()
        return
      }
    }

    this.executeImportUpload()
  }

  /**
   * Execute the import upload after balance checks pass.
   * Member imports post to import-members instead of the regular upload.
   */
  private executeImportUpload(): void {
    try {
      const { eventSlug, formSlug, workspace } = this.getUploadParameters()
      if (!workspace || !this.file) return

      this.isImporting = true
      this.emailCheckForcedOn.set(false)
      if (this.isMemberImport()) {
        this.importMembers(eventSlug)
        return
      }
      const payload = this.createUploadPayload(this.file)
      this.executeUpload(eventSlug, formSlug, payload)
    } catch (error) {
      this.isImporting = false
      this.handleUploadError(error)
    }
  }

  /** Posts the file under the selected parent. Email validation follows the admin checkbox. */
  private importMembers(eventSlug: string): void {
    const parentSlug = this.parentPersona()?.slug
    const parent = this.selectedParent()
    if (!parentSlug || !parent || !this.file) {
      this.isImporting = false
      return
    }

    const skipEmailCheck = this.shouldSkipEmailCheck()
    this.requestedSkipEmailCheck = skipEmailCheck
    this.databaseService
      .importGroupMembers(eventSlug, parentSlug, {
        file: this.file,
        parentId: parent.id,
        dataMapping: this.buildDataMapping(),
        checkEmail: !skipEmailCheck
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleUploadSuccess(response),
        error: (error) => this.handleUploadError(error)
      })
  }

  /**
   * Get required parameters for upload
   */
  private getUploadParameters(): { enable_communication: boolean; eventSlug: string; formSlug: string; workspace: string | null } {
    if (!this.registrationsDataSourceCollection) {
      throw new Error('Data source collection is not available')
    }

    const selectedIndex = this.registrationsDataSourceCollection.selectedIndex || 0
    const currentPersona = this.registrationsDataSourceCollection.registrationsDataSources[selectedIndex]
    if (!currentPersona) {
      throw new Error('Selected persona is not available')
    }

    const firstDataSource = this.registrationsDataSourceCollection.registrationsDataSources[0]
    if (!firstDataSource?.registrationsDataSource?.initializeRequest) {
      throw new Error('Data source or initialization data is not available')
    }

    return {
      enable_communication: currentPersona.registrationsDataSource.form.enable_communication || false,
      eventSlug: firstDataSource.registrationsDataSource.initializeRequest.event,
      formSlug: currentPersona.slug,
      workspace: this.databaseService.getWorkspaceSlug()
    }
  }

  /**
   * Create upload payload. Column mapping is shared with member import.
   */
  private createUploadPayload(file: File): any {
    return {
      data_mapping: this.buildDataMapping(),
      file: file,
      send_email: this.confirmationForm.get('sendConfirmationEmail')?.value ? 1 : 0,
      send_whatsapp: this.canSendPhoneConfirmations && this.confirmationForm.get('sendWhatsappConfirmation')?.value ? 1 : 0,
      send_sms: this.canSendPhoneConfirmations && this.confirmationForm.get('sendSmsConfirmation')?.value ? 1 : 0,
      check_email: this.shouldSkipEmailCheck() ? 0 : 1
    }
  }

  /** Super admins skip email validation only when the review checkbox is checked. */
  private shouldSkipEmailCheck(): boolean {
    return this.isSuperAdmin && !!this.confirmationForm.get('bypassEmailValidation')?.value
  }

  /** Maps sanitized file columns onto persona fields for both import paths. */
  private buildDataMapping(): Record<string, string> {
    return this.columnsMap.reduce((acc, mapping) => {
      const sanitizedSource = this.sanitizeColumnName(mapping.sourceColumn)
      if (sanitizedSource && sanitizedSource !== '-- Skip this field --') {
        acc[sanitizedSource] = mapping.targetColumn
      }
      return acc
    }, {} as Record<string, string>)
  }

  /**
   * Sanitize source column names to avoid trailing CR/LF, quotes, and spaces
   */
  private sanitizeColumnName(name: string): string {
    if (!name) return ''
    return name
      .trim()
      .replace(/\uFEFF/g, '') // BOM
      .replace(/\u00A0/g, '') // NBSP -> normal space
      .replace(/[\r\n\t]/g, '') // CR, LF, TAB
      .replace(/[\u200B-\u200D\u2060]/g, '') // zero-width chars
      .replace(/^["']|["']$/g, '') // Remove leading and trailing quotes
  }

  /**
   * Execute the upload request
   */
  private executeUpload(eventSlug: string, formSlug: string, payload: any): void {
    this.databaseService
      .uploadImportFile(eventSlug, formSlug, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleUploadSuccess(response),
        error: (error) => this.handleUploadError(error)
      })
  }

  /**
   * Handle successful upload.
   * Member imports keep the upload id and warn when email validation could not be skipped.
   */
  private handleUploadSuccess(response: any): void {
    this.isImporting = false
    const upload = response?.upload
    if (this.isMemberImport() && upload) {
      this.memberImportUploadId = upload.id
      const returnedCheck = upload.check_email
      const emailCheckOn = returnedCheck === 1 || returnedCheck === true || returnedCheck === '1'
      if (this.requestedSkipEmailCheck && emailCheckOn) {
        this.emailCheckForcedOn.set(true)
        this.toastrService.warning('Email validation stayed on. This account cannot skip it.', 'Email check')
      }
    }
    this.setCurrentUpload(upload, 'upload')
    this.viewUploadDetails(upload)
    this.refreshUploadHistory()
  }

  /**
   * View details of a specific upload.
   * A finished member import refreshes the selected parent's member count.
   */
  viewUploadDetails(upload: UploadStatus): void {
    this.selectedUpload = upload
    // Set currentUpload with 'view' source when user clicks to view details
    this.setCurrentUpload(upload, 'view')
    if (!this.getUploadState(upload).isSaved) {
      this.updateUploadProgress(upload.id)
    } else if (this.memberImportUploadId === upload.id) {
      this.refreshSelectedParentCount()
    }
    // Always switch to progress tab
    this.activeTab = TabView.UPLOAD_PROGRESS
  }

  /**
   * Update the upload progress for a specific upload.
   * Member imports poll import-members for the selected parent instead of /upload/{id}.
   */
  updateUploadProgress(uploadId: number): void {
    if (this.memberImportUploadId === uploadId && this.selectedParent() && this.parentPersona()) {
      this.pollMemberImport(uploadId)
      return
    }
    // Clean up any existing subscription
    if (this.uploadStatusSubscription) {
      this.uploadStatusSubscription.unsubscribe()
      this.uploadStatusSubscription = null
    }

    // Reset progress tracking
    this.previousProgress = 0

    this.uploadStatusSubscription = this.databaseService
      .getUploadStatus(uploadId)
      .pipe(
        repeatWhen((notifier) =>
          notifier.pipe(
            delay(3000) // Poll every 3 seconds
          )
        ),
        takeWhile((response: any) => {
          if (!response?.upload) return false

          const state = this.getUploadState(response.upload)
          // Stop polling if:
          // 1. Upload is completed (SAVED) OR
          // 2. Upload is stopped (CLOSED) OR
          // 3. User manually stopped the upload
          const shouldStop = state.isSaved || state.isStopped || this.isStoppedByUser

          if (shouldStop) {
            // Process final state immediately
            this.handleUploadStatusUpdate(response)
            this.cdr.detectChanges()
          }

          return !shouldStop
        }, true),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => this.handleUploadStatusUpdate(response),
        error: (error) => this.handleUploadStatusError(error),
        complete: () => {
          if (this.uploadStatusSubscription) {
            this.uploadStatusSubscription.unsubscribe()
            this.uploadStatusSubscription = null
          }
        }
      })
  }

  /**
   * Handle upload status update response
   */
  private handleUploadStatusUpdate(response: any): void {
    if (!response?.upload) return

    const newUpload = response.upload
    const currentState = this.currentUpload?.status?.state
    const newState = newUpload.status?.state
    const uploadFinished = currentState !== UploadState.SAVED && newState === UploadState.SAVED

    // Always update if we have a new state, even if it's the same as current
    // This ensures we process the SAVED state properly
    if (newState) {
      // Update currentUpload if this is the active upload
      if (this.currentUpload && newUpload.id === this.currentUpload.id) {
        // Create a new object to ensure change detection
        this.currentUpload = { ...newUpload }
        this.lastUploadId = newUpload.id.toString()
        const statusInfo = this.getUploadStatusInfo(newUpload)
        this.uploadProgress = statusInfo.progress

        // Update isUploadInProgress based on the actual state
        const state = this.getUploadState(newUpload)
        this.isUploadInProgress = state.isInProgress

        // Update selectedUpload if this is an active upload
        if (state.isInProgress || state.isSaved) {
          this.selectedUpload = { ...newUpload }
        }
      }

      // Update uploadHistory
      this.uploadHistory = this.uploadHistory.map((u) => (u.id === newUpload.id ? { ...newUpload } : u))

      // Import + confirmations debit the wallet when the upload finishes saving
      if (uploadFinished) {
        this.navbarService.refreshBalance()
      }

      // Force change detection
      this.cdr.detectChanges()
    }
  }

  /**
   * Refresh the upload history.
   * Member imports load history from the parent group form, not the member form.
   */
  refreshUploadHistory(): void {
    try {
      const { eventSlug, formSlug, workspace } = this.getUploadParameters()
      if (!workspace) return
      const parentSlug = this.parentPersona()?.slug
      const historyFormSlug = this.isMemberImport() && parentSlug ? parentSlug : formSlug

      this.isLoadingMore = true
      this.databaseService
        .getUploadHistory(workspace, eventSlug, historyFormSlug)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: UploadHistoryResponse) => {
            // Update pagination state
            this.last_page = response.last_page
            this.total = response.total
            this.per_page = response.per_page
            this.current_page = response.current_page

            // Update and sort history
            this.uploadHistory = (response.uploads || []).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

            // Check for in-progress uploads
            const inProgressUploads = this.uploadHistory.filter((upload) => {
              const state = this.getUploadState(upload)
              return !state.isSaved
            })

            if (inProgressUploads.length > 0) {
              // Only update currentUpload if there is a new in-progress upload
              if (!this.currentUpload || this.currentUpload.id !== inProgressUploads[0].id) {
                this.setCurrentUpload(inProgressUploads[0], 'background')
              }
            }

            this.isLoadingMore = false
            this.cdr.detectChanges()
          },
          error: (error) => {
            this.handleUploadHistoryError(error)
            this.isLoadingMore = false
            this.cdr.detectChanges()
          }
        })
    } catch (error) {
      this.handleUploadHistoryError(error)
      this.isLoadingMore = false
      this.cdr.detectChanges()
    }
  }

  /**
   * Handle upload history error
   */
  private handleUploadHistoryError(error: any): void {
    this.isUploadInProgress = false
    console.error('Error refreshing upload history:', error)
  }

  /**
   * Handle upload error and show the API message, including member-import failures.
   */
  private handleUploadError(error: any): void {
    this.isImporting = false
    console.error('Error during upload:', error)
    this.toastrService.danger(this.extractErrorMessage(error), 'Import failed')
    this.cdr.detectChanges()
  }

  /** Reads a message from an import error body. */
  private extractErrorMessage(error: any): string {
    const body = error?.error
    if (typeof body === 'string' && body.trim()) return body
    if (typeof body?.message === 'string' && body.message.trim()) return body.message
    if (Array.isArray(body?.message)) return body.message.join(' ')
    if (typeof error?.message === 'string' && error.message.trim()) return error.message
    return 'An error occurred during the import.'
  }

  /** Polls the in-flight member import until it is saved, closed, or failed. */
  private pollMemberImport(uploadId: number): void {
    if (this.uploadStatusSubscription) {
      this.uploadStatusSubscription.unsubscribe()
      this.uploadStatusSubscription = null
    }
    this.previousProgress = 0
    const parent = this.selectedParent()
    const parentSlug = this.parentPersona()?.slug
    if (!parent || !parentSlug) return
    const { eventSlug } = this.getUploadParameters()

    this.uploadStatusSubscription = this.databaseService
      .getMemberImportStatus(eventSlug, parentSlug, parent.id)
      .pipe(
        repeatWhen((notifier) => notifier.pipe(delay(4000))),
        takeWhile((response: any) => {
          const upload = response?.upload
          if (!upload || Number(upload.id) !== Number(uploadId)) return true
          return !this.isTerminalMemberState(upload.status?.state)
        }, true),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          const upload = response?.upload
          if (!upload || Number(upload.id) !== Number(uploadId)) return
          this.handleUploadStatusUpdate(response)
          const state = upload.status?.state
          if (state === UploadState.SAVED) this.refreshSelectedParentCount()
          if (!this.isStoppedByUser && (state === UploadState.ERROR || state === UploadState.FAILED || state === UploadState.CLOSED)) {
            this.toastrService.danger('The member import failed.', 'Import failed')
          }
        },
        error: (error) => this.handleUploadStatusError(error),
        complete: () => {
          if (this.uploadStatusSubscription) {
            this.uploadStatusSubscription.unsubscribe()
            this.uploadStatusSubscription = null
          }
        }
      })
  }

  /** States that end member-import polling. */
  private isTerminalMemberState(state: string | undefined): boolean {
    return state === UploadState.SAVED || state === UploadState.CLOSED || state === UploadState.ERROR || state === UploadState.FAILED
  }

  /** Reloads the selected parent so its member count includes the import. */
  private refreshSelectedParentCount(): void {
    const parent = this.selectedParent()
    const parentSlug = this.parentPersona()?.slug
    if (!parent || !parentSlug) return
    const { eventSlug } = this.getUploadParameters()
    this.databaseService
      .getParentRegistration(eventSlug, parentSlug, parent.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          if (!updated) return
          this.selectedParent.set(updated)
          this.parentResults.update((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))
        }
      })
  }

  uploadAnotherFile(uploadId: number) {
    this.currentStep = 1
    this.stopUpload(uploadId)
    this._reset()
  }
  /**
   * Stop an ongoing upload
   * @param uploadId The ID of the upload to stop
   * @param event Optional mouse event to stop propagation
   */
  stopUpload(uploadId: number, event?: MouseEvent): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Show confirmation dialog
    if (confirm('Are you sure you want to stop this upload? This action cannot be undone.')) {
      // Disable further stop actions by marking as complete
      if (this.currentUpload && this.currentUpload.id === uploadId) {
        // Immediately update UI to reflect stopping state
        this.currentUpload.status.state = UploadState.CLOSED
        this.isUploadInProgress = false

        // Update display in history as well
        const historyUpload = this.uploadHistory.find((u) => u.id === uploadId)
        if (historyUpload) {
          historyUpload.status.state = UploadState.CLOSED
        }
      }

      this.isStoppedByUser = true
      if (this.uploadStatusSubscription) {
        this.uploadStatusSubscription.unsubscribe()
        this.uploadStatusSubscription = null
      }
      this.databaseService
        .stopUpload(uploadId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            // Refresh data immediately
            this.refreshUploadHistory()
          },
          (error) => {
            console.error('Error stopping upload:', error)
            this.toastrService.danger('Failed to stop upload', 'Error')

            // Revert UI changes if API call failed
            if (this.currentUpload && this.currentUpload.id === uploadId) {
              this.updateUploadProgress(uploadId)
            }
          }
        )
    }
  }

  /**
   * Download invalid records from an upload
   * @param uploadId The ID of the upload
   * @param event Optional mouse event to stop propagation
   */
  downloadInvalidRecords(upload: UploadStatus, event?: MouseEvent): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.databaseService
      .downloadInvalidRecords(upload?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          try {
            // Extract upload data and invalidLines from the response
            const uploadData = response.upload || {}
            const invalidLines = uploadData.invalidLines || []

            if (invalidLines.length === 0) {
              return
            }

            // Get headers from the first record's "record" property keys
            let headers: string[] = []
            if (invalidLines.length > 0 && invalidLines[0].record) {
              headers = Object.keys(invalidLines[0].record)
            }

            // Add an error column
            headers.push('Validation Errors')

            // Create CSV content
            let csvContent = headers.join(',') + '\n'

            // Add rows
            invalidLines.forEach((line) => {
              const record = line.record || {}
              const errorMessages: string[] = []

              // Extract errors - they are nested in errors.data object
              if (line.errors) {
                if (line.errors.data) {
                  // Extract error messages from the data object
                  Object.keys(line.errors.data).forEach((key) => {
                    const errorText = line.errors.data[key]
                    // Keep the original error message format
                    errorMessages.push(errorText)
                  })
                } else if (line.errors.error) {
                  // Use the general error message if data is not available
                  errorMessages.push(line.errors.error)
                }
              }

              // Create an array for this row's values
              const rowValues = headers.map((header) => {
                if (header === 'Validation Errors') {
                  // Join errors with newlines to make them more readable
                  return `"${errorMessages.join('\n').replace(/"/g, '""')}"`
                } else {
                  // Get the value for this header from the record
                  const value = record[header] || ''
                  // Escape quotes and handle commas
                  return typeof value === 'string' && (value.includes(',') || value.includes('"')) ? `"${value.replace(/"/g, '""')}"` : value
                }
              })

              // Add the row to CSV content
              csvContent += rowValues.join(',') + '\n'
            })

            // Create a blob from the CSV content
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

            // Create a download link
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `invalid-records-${upload.form.event}-${upload.form.form}-${new Date().toLocaleDateString('en-GB').replace(/[/:]/g, '-')}-${new Date()
              .toLocaleTimeString('en-GB')
              .replace(/[/:]/g, '-')
              .replace(/\s/g, '')}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          } catch (error) {
            console.error('Error processing invalid records:', error)
            this.toastrService.danger('Failed to process invalid records', 'Error')
          }
        },
        (error) => {
          console.error('Error downloading invalid records:', error)
          this.toastrService.danger('Failed to download invalid records', 'Error')
        }
      )
  }

  /**
   * Send invitation emails for an upload
   * @param upload The upload object
   * @param event Optional mouse event to stop propagation
   */
  sendConfirmationEmails(upload: UploadStatus, event?: MouseEvent): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Show confirmation dialog
    if (confirm('Are you sure you want to send confirmation emails to all valid records in this import?')) {
      this.databaseService
        .sendConfirmationEmails(upload.id, {
          workspace_slug: this.databaseService.getWorkspaceSlug(),
          event_slug: upload.form.event
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            this.toastrService.success('Confirmation emails sent successfully', 'Success')
            this.navbarService.refreshBalance()
          },
          (error) => {
            console.error('Error sending confirmation emails:', error)
            this.toastrService.danger('Failed to send confirmation emails', 'Error')
          }
        )
    }
  }

  /**
   * Get the current upload state.
   * Error and failed states count as stopped so a member-import failure is not shown as in progress.
   */
  getUploadState(upload: UploadStatus | null): UploadStateInfo {
    if (!upload || !upload.status) {
      return {
        isUploaded: false,
        isCompiling: false,
        isCompiled: false,
        isSaving: false,
        isSaved: false,
        isClose: false,
        isStopped: false,
        isInProgress: false
      }
    }

    const state = upload.status.state

    if (state === UploadState.ERROR || state === UploadState.FAILED) {
      return {
        isUploaded: false,
        isCompiling: false,
        isCompiled: false,
        isSaving: false,
        isSaved: false,
        isClose: false,
        isStopped: true,
        isInProgress: false
      }
    }

    // Define active states that indicate upload is in progress
    const activeStates = [UploadState.UPLOADED, UploadState.COMPILING, UploadState.COMPILED, UploadState.SAVING]

    // Check if we're in a stopping state
    const isStopping = state === UploadState.CLOSE
    const isStopped = state === UploadState.CLOSED
    const isSaved = state === UploadState.SAVED

    // If we're stopping or stopped, override other states
    if (isStopping || isStopped) {
      return {
        isUploaded: false,
        isCompiling: false,
        isCompiled: false,
        isSaving: false,
        isSaved: false,
        isClose: isStopping,
        isStopped: isStopped,
        isInProgress: isStopping // Only in progress if we're in CLOSE state
      }
    }

    // Normal state progression
    return {
      isInProgress: activeStates.includes(state),
      isUploaded: state === UploadState.UPLOADED,
      isCompiling: state === UploadState.COMPILING,
      isCompiled: state === UploadState.COMPILED,
      isSaving: state === UploadState.SAVING,
      isSaved: isSaved,
      isClose: false,
      isStopped: false
    }
  }

  /**
   * Get upload status information.
   * Error and failed uploads are labeled Failed.
   */
  getUploadStatusInfo(upload: UploadStatus | null): UploadStatusInfo {
    if (!upload || !upload.status) {
      return {
        progress: 0,
        statusLabel: 'Unknown',
        statusClass: 'status-unknown'
      }
    }

    const state = this.getUploadState(upload)
    let statusLabel = 'Unknown'
    let statusClass = 'status-unknown'
    const rawState = upload.status.state

    if (rawState === UploadState.ERROR || rawState === UploadState.FAILED) {
      statusLabel = 'Failed'
      statusClass = 'error'
    } else if (state.isSaved) {
      statusLabel = 'Completed'
      statusClass = 'success'
    } else if (state.isUploaded || state.isCompiling || state.isCompiled || state.isSaving) {
      statusLabel = 'In Progress'
      statusClass = 'warning'
    } else if (state.isClose) {
      statusLabel = 'Stopping...'
      statusClass = 'error'
    } else if (state.isStopped) {
      statusLabel = 'Stopped'
      statusClass = 'error'
    } else {
      statusLabel = 'Unknown'
      statusClass = 'unknown'
    }

    return {
      progress: this.calculateUploadProgress(upload),
      statusLabel,
      statusClass
    }
  }

  updatePaginatedData() {
    const start = (this.current_page - 1) * this.per_page
    const end = start + this.per_page
    this.uploadHistory = this.uploadHistory.slice(start, end)
  }

  nextPage(): void {
    if (this.current_page < this.last_page && !this.isLoadingMore) {
      this.current_page++
      this.refreshUploadHistory()
    }
  }

  prevPage(): void {
    if (this.current_page > 1 && !this.isLoadingMore) {
      this.current_page--
      this.refreshUploadHistory()
    }
  }

  get totalPages(): number {
    return this.last_page
  }

  // Add method to reset progress when needed
  private resetProgress(): void {
    this.previousProgress = 0
  }
}
